// URL mappings for Dutch localization
export const routeMappings = {
  en: {
    "/": "/",
    "/about": "/about",
    "/services": "/services",
    "/booking": "/booking",
    "/contact": "/contact",
    "/city-transfers": "/city-transfers",
    "/tours/giethoorn": "/tours/giethoorn",
    "/tours/bruges": "/tours/bruges",
    "/tours/keukenhof": "/tours/keukenhof",
    "/tours/marken": "/tours/marken",
    "/tours/volendam": "/tours/volendam",
    "/tours/zaanse-schans": "/tours/zaanse-schans",
    "/tours/brussels": "/tours/brussels",
    "/tours/antwerp": "/tours/antwerp",
  },
  nl: {
    "/": "/",
    "/about": "/over-ons",
    "/services": "/diensten",
    "/booking": "/boeken",
    "/contact": "/contact",
    "/city-transfers": "/stad-transfers",
    "/tours/giethoorn": "/tours/giethoorn",
    "/tours/bruges": "/tours/brugge",
    "/tours/keukenhof": "/tours/keukenhof",
    "/tours/marken": "/tours/marken",
    "/tours/volendam": "/tours/volendam",
    "/tours/zaanse-schans": "/tours/zaanse-schans",
    "/tours/brussels": "/tours/brussel",
    "/tours/antwerp": "/tours/antwerpen",
  },
} as const;

// Reverse mapping for route matching
export const reverseRouteMappings = {
  nl: Object.entries(routeMappings.nl).reduce((acc, [key, value]) => {
    acc[value] = key;
    return acc;
  }, {} as Record<string, string>),
};

export const supportedLanguages = ["en", "nl"] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

// Get localized path for current language
export const getLocalizedPath = (
  path: string,
  language: SupportedLanguage
): string => {
  return routeMappings[language][path as keyof typeof routeMappings.en] || path;
};

// Get base path from localized path (for Dutch)
export const getBasePath = (
  localizedPath: string,
  language: SupportedLanguage
): string => {
  if (language === "en") return localizedPath;
  return reverseRouteMappings.nl[localizedPath] || localizedPath;
};

// Extract language and path from URL
export const parseLanguageFromPath = (
  pathname: string
): { language: SupportedLanguage; path: string } => {
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return { language: "en", path: "/" };
  }

  const firstSegment = segments[0];

  if (supportedLanguages.includes(firstSegment as SupportedLanguage)) {
    const language = firstSegment as SupportedLanguage;
    const path = "/" + segments.slice(1).join("/");
    return { language, path: path === "/" ? "/" : path };
  }

  return { language: "en", path: pathname };
};

// Build full URL with language prefix
export const buildLanguageUrl = (
  path: string,
  language: SupportedLanguage
): string => {
  const localizedPath = getLocalizedPath(path, language);
  return `/${language}${localizedPath === "/" ? "" : localizedPath}`;
};
